/**
 * DTHDSCLayout.jsx
 *
 * DTH-specific composition of the Drill String Configurator. REPLACES the
 * SP2 placeholder stub (`DSCConfigurator.jsx` is shared; this layout owns
 * the DTH-only branch per brainstorm "DTH DSC — captured 19 May 2026"
 * + plan §SP3.1).
 *
 * Structural order (top to bottom):
 *   1. Header — page title + `<DescriptionCard />`. Quieter Sandvik
 *      info-card per brainstorm wishlist #12; NOT orange-bordered.
 *   2. Top strip — `<UnitToggle />` (Metric/Imperial — DTH-only) +
 *      `<RestartConfiguratorButton />`.
 *   3. `<EntryPointTabs />` — two-card affordance for Hole-size-first vs
 *      Hammer-Series-first entry (brainstorm DTH #1).
 *   4. Slot card row — three `<DSCSlotCard />` instances in canonical card
 *      order: **DTH Bit · DTH Hammer · DTH Drillpipe**. Stacks vertically
 *      under `lg` (1024px) per brainstorm DTH #5 — drilling is physically
 *      vertical, vertical-on-mobile is the locked default.
 *   5. `<DSCResultsTabs />` — tab nav driven from
 *      `dscSlotConfig.DTH_TAB_ORDER` which enforces **DTH Hammers →
 *      DTH Drill pipes → DTH Bits** (canonical §1.3, corrects the legacy
 *      DOM order in `searchResults.html:234-242`).
 *   6. `<DSCResultsTabPanel />` for the active tab.
 *
 * Hammer Series dropdown — RH570 surfaces ABOVE RH560: the ranking lives
 * in `dscSlotConfig.hammerFamilyRank` and is APPLIED inside
 * `services/dscFilteringData.js` when building the
 * `initialDropdownOptions['hammer-series:family']` map. This layout
 * consumes the pre-sorted options verbatim from
 * `useDSCSlot('hammer-series').options.family`. Plan §SP3.3 + brainstorm
 * DTH #2.
 *
 * DTH Hammer slot card hosts TWO dropdowns (Hammer Series + Shank type) —
 * both rendered as children of a single `<DSCSlotCard>` per legacy
 * `searchResults.html` lines 230-242 + plan §SP3.1 dropdown-wiring table.
 *
 * Citations (port-first):
 *  - Legacy DOM: `My%20Rock%20Tools/force-app/main/default/lwc/searchResults/
 *    searchResults.html` lines 199-312. We INTENTIONALLY DIVERGE in three
 *    places, each brainstorm-backed:
 *       a) Tab order corrected — Hammers → Drill pipes → Bits (DTH #3).
 *       b) Two entry points surfaced as explicit cards (DTH #1).
 *       c) Vertical-on-mobile layout (DTH wishlist #5).
 *  - Legacy cascade: `searchResults.js:1450-1706` — `reverseFilter` for the
 *    two entry points. PWA cascade `services/dscCascade.js:383` INFERS
 *    `reverseFilter` from whether `hammer-series.family` is filled, so this
 *    UI does not need to mutate cascade state directly.
 *  - Brainstorm DTH live capture (lines 141-153) — page layout, slot order,
 *    dropdown copy.
 *  - Plan §SP3.1, §SP3.5, §SP3.6, §SP3.7.
 *
 * Entry-point state model:
 *  - `dthEntryPoint` lives in LOCAL `useState` (with sessionStorage
 *    backing) — NOT in `DSCContext`. Rationale: cascade already infers
 *    `reverseFilter` from selection order; lifting this into DSCContext
 *    would be redundant. Per task brief: "preferred — keeps scope clean."
 *  - Vandana may later promote into DSCContext if she wants URL deep-link
 *    behaviour for the entry point itself — flagged in the SP3 report.
 *
 * @module components/dsc/DTHDSCLayout
 * @since 2026-05-20 (Group E SP3.1 / SP3.5 / SP3.6 / SP3.7)
 */

import { useState, useEffect, useMemo, useCallback } from 'react';

import { useDSC, useDSCSlot } from '@contexts/DSCContext';
import { useCart } from '@contexts/CartContext';
import useAuth from '@hooks/useAuth';
import useMediaQuery from '@hooks/useMediaQuery';
import { filterProductsForTab } from '@services/dscTabFilter';
import { DSC_SLOT_CONFIG, DSC_TAB_FACETS } from '@data/dscSlotConfig';

import DSCSlotCard from '@components/dsc/DSCSlotCard';
import SlotChip from '@components/dsc/SlotChip';
import DSCDropdownRow from '@components/dsc/DSCDropdownRow';
import DSCResultsTabs from '@components/dsc/DSCResultsTabs';
import DSCResultsTabPanel from '@components/dsc/DSCResultsTabPanel';
import DescriptionCard from '@components/dsc/DescriptionCard';
import RestartConfiguratorButton from '@components/dsc/RestartConfiguratorButton';
import UnitToggle from '@components/dsc/UnitToggle';
import MiniCart from '@components/layout/MiniCart';
import EntryPointTabs, {
  DTH_ENTRY_HOLE_SIZE_FIRST,
  DTH_ENTRY_HAMMER_FIRST,
} from '@components/dsc/EntryPointTabs';

// ===========================================================================
// Static copy — DescriptionCard body for DTH (brainstorm wishlist #12).
// ===========================================================================

const DTH_DESCRIPTION_TITLE = 'Down-the-hole Drill string configurator';
const DTH_DESCRIPTION_BODY =
  'Build a compatible DTH drill string from the bit at the hole bottom up to ' +
  'the hammer and drill pipe at the rig. Start with either the hole size you ' +
  'want to drill or the Hammer Series you already have — both paths converge ' +
  'on the same compatible product set. Switch between Metric and Imperial at ' +
  'any time; your selections are preserved.';

const ENTRY_POINT_SESSION_KEY = 'mrt.dsc.entryPoint.dth';

// ===========================================================================
// Per-slot dropdown panels — small internal components that read from
// useDSCSlot() and emit DSCDropdownRow.
// ===========================================================================

function DTHBitDropdowns({ disabled }) {
  const slot = useDSCSlot('dth-bit');
  const slotCfg = DSC_SLOT_CONFIG.DTH['dth-bit'];

  const holeSizeOptions = slot.options.holeSize || slot.options.bitDiameter || [];
  const value = slot.value?.holeSize
    ? { label: String(slot.value.holeSize), value: String(slot.value.holeSize) }
    : null;

  const onChange = useCallback(
    (item) => slot.setSelection('holeSize', item ? item.value : null),
    [slot]
  );

  return (
    <DSCDropdownRow
      question={slotCfg.dropdownLabel}
      items={holeSizeOptions}
      value={value}
      onChange={onChange}
      disabled={disabled || slot.isDisabled}
      placeholder="Select hole size..."
      dataTestid="dth-bit-dropdown"
    />
  );
}

function DTHHammerDropdowns({ disabled }) {
  const slot = useDSCSlot('hammer-series');
  const slotCfg = DSC_SLOT_CONFIG.DTH['hammer-series'];

  // RH570 ranking is applied UPSTREAM in dscFilteringData.js — we consume
  // the pre-sorted list verbatim. Plan §SP3.3 + brainstorm DTH #2.
  const familyOptions = slot.options.family || [];
  const shankOptions = slot.options.shank || [];

  const familyValue = slot.value?.family
    ? { label: slot.value.family, value: slot.value.family }
    : null;
  const shankValue = slot.value?.shank
    ? { label: slot.value.shank, value: slot.value.shank }
    : null;

  const onFamilyChange = useCallback(
    (item) => slot.setSelection('family', item ? item.value : null),
    [slot]
  );
  const onShankChange = useCallback(
    (item) => slot.setSelection('shank', item ? item.value : null),
    [slot]
  );

  return (
    <div className="flex flex-col gap-3">
      <DSCDropdownRow
        question={slotCfg.dropdownLabel}
        items={familyOptions}
        value={familyValue}
        onChange={onFamilyChange}
        disabled={disabled}
        placeholder="Select Hammer Series..."
        dataTestid="dth-hammer-family-dropdown"
      />
      <DSCDropdownRow
        question={slotCfg.secondaryDropdownLabel}
        items={shankOptions}
        value={shankValue}
        onChange={onShankChange}
        // Disable when: (a) no options to choose from, OR (b) the cascade
        // auto-selected the only shank type (hammer-first flow where the
        // family label already encodes a single shank — e.g. RH570-5.5
        // yields only 570-5.5). Eliminates a redundant click per Vandana
        // 21 May walkthrough.
        disabled={disabled || shankOptions.length === 0 || !!slot.value?.shankAutoSelected}
        placeholder="Select shank type..."
        dataTestid="dth-hammer-shank-dropdown"
      />
    </div>
  );
}

function DTHDrillpipeDropdowns({ disabled }) {
  const slot = useDSCSlot('drillpipe');
  const slotCfg = DSC_SLOT_CONFIG.DTH.drillpipe;

  const pinOptions = slot.options.pinOption || [];
  const value = slot.value?.pinOption
    ? { label: slot.value.pinOption, value: slot.value.pinOption }
    : null;

  const onChange = useCallback(
    (item) => slot.setSelection('pinOption', item ? item.value : null),
    [slot]
  );

  return (
    <DSCDropdownRow
      question={slotCfg.dropdownLabel}
      items={pinOptions}
      value={value}
      onChange={onChange}
      disabled={disabled || slot.isDisabled}
      placeholder="Select pin option..."
      dataTestid="dth-drillpipe-dropdown"
    />
  );
}

// ===========================================================================
// DTHDSCLayout — the exported composition.
// ===========================================================================

/**
 * @param {Object} [props]
 * @param {string} [props.className]
 * @param {string} [props.dataTestid]
 */
export default function DTHDSCLayout({
  className = '',
  dataTestid = 'dth-dsc-layout',
}) {
  const dsc = useDSC();

  // Entry-point: local state + sessionStorage backing. Not in DSCContext.
  const [dthEntryPoint, setDthEntryPointState] = useState(() => {
    try {
      if (typeof window === 'undefined' || !window.sessionStorage) {
        return DTH_ENTRY_HOLE_SIZE_FIRST;
      }
      const raw = window.sessionStorage.getItem(ENTRY_POINT_SESSION_KEY);
      if (raw === DTH_ENTRY_HAMMER_FIRST || raw === DTH_ENTRY_HOLE_SIZE_FIRST) {
        return raw;
      }
      return DTH_ENTRY_HOLE_SIZE_FIRST;
    } catch {
      return DTH_ENTRY_HOLE_SIZE_FIRST;
    }
  });

  const setDthEntryPoint = useCallback((next) => {
    setDthEntryPointState(next);
    // UX fix (21 May): switching entry point auto-restarts the cascade so
    // the user gets a clean slate. Without this, stale selections from the
    // previous entry path block the new path's dropdowns from populating.
    dsc.restart();
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        window.sessionStorage.setItem(ENTRY_POINT_SESSION_KEY, next);
      }
    } catch {
      // sessionStorage quota / privacy mode — ignore.
    }
  }, [dsc]);

  // Active tab — defaults to the first DTH tab id (Hammers per canonical §1.3).
  const [activeTab, setActiveTab] = useState(
    () => (dsc.tabOrder && dsc.tabOrder[0]) || 'dth-hammers'
  );
  useEffect(() => {
    if (!dsc.tabOrder || dsc.tabOrder.length === 0) return;
    if (!dsc.tabOrder.includes(activeTab)) setActiveTab(dsc.tabOrder[0]);
  }, [dsc.tabOrder, activeTab]);

  // Per-slot subscriptions for chip rendering and disabled flags.
  const dthBitSlot = useDSCSlot('dth-bit');
  const hammerSlot = useDSCSlot('hammer-series');
  const drillpipeSlot = useDSCSlot('drillpipe');

  const dthBitChipLabel = useMemo(
    () => DSC_SLOT_CONFIG.DTH['dth-bit'].filledChipFormat(dthBitSlot.value),
    [dthBitSlot.value]
  );
  const hammerChipLabel = useMemo(
    () => DSC_SLOT_CONFIG.DTH['hammer-series'].filledChipFormat(hammerSlot.value),
    [hammerSlot.value]
  );
  const drillpipeChipLabel = useMemo(
    () => DSC_SLOT_CONFIG.DTH.drillpipe.filledChipFormat(drillpipeSlot.value),
    [drillpipeSlot.value]
  );

  const clearBit = useCallback(() => dthBitSlot.clear?.(), [dthBitSlot]);
  const clearHammer = useCallback(() => hammerSlot.clear?.(), [hammerSlot]);
  const clearDrillpipe = useCallback(() => drillpipeSlot.clear?.(), [drillpipeSlot]);

  // Visual de-emphasis based on entry point. Brainstorm Q5: no auto-clear.
  const isHammerFirst = dthEntryPoint === DTH_ENTRY_HAMMER_FIRST;
  const isHoleSizeFirst = dthEntryPoint === DTH_ENTRY_HOLE_SIZE_FIRST;

  // Hole-size-first: disable Hammer-Series chain UNTIL bit picked.
  // Hammer-Series-first: disable Bit chain UNTIL hammer family picked.
  const disableBitChain = isHammerFirst && !hammerSlot.value?.family;
  const disableHammerChain = isHoleSizeFirst && !dthBitSlot.value?.holeSize;

  return (
    <div
      data-testid={dataTestid}
      className={`flex flex-col gap-6 ${className}`}
    >
      {/* 1. Header — page title + DescriptionCard */}
      <div
        data-testid="dth-header"
        className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between"
      >
        <div className="flex-1">
          <h1 className="font-sandvik-headline text-2xl lg:text-3xl text-carbon-black">
            {DTH_DESCRIPTION_TITLE}
          </h1>
        </div>
        <div className="lg:max-w-md w-full">
          <DescriptionCard
            title="How to use this configurator"
            body={DTH_DESCRIPTION_BODY}
          />
        </div>
      </div>

      {/* 2. Top strip — UnitToggle (left) + RestartConfiguratorButton (right) */}
      <div
        data-testid="dth-top-strip"
        className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between"
      >
        <UnitToggle />
        <RestartConfiguratorButton />
      </div>

      {/* 3. EntryPointTabs — two-card affordance (brainstorm DTH #1) */}
      <EntryPointTabs
        value={dthEntryPoint}
        onChange={setDthEntryPoint}
      />

      {/* 4. Slot card row — vertical < lg, horizontal >= lg
            (brainstorm DTH #5 — drilling is physically vertical). */}
      <div
        data-testid="dth-slot-row"
        className="flex flex-col gap-4 lg:flex-row lg:gap-6 lg:items-stretch"
      >
        <DSCSlotCard
          slotId="dth-bit"
          dataTestid="dsc-slot-card-dth-bit"
          image={DSC_SLOT_CONFIG.DTH['dth-bit'].image}
          title={DSC_SLOT_CONFIG.DTH['dth-bit'].title}
          isFilled={!!dthBitChipLabel}
          isDisabled={disableBitChain}
          filledLabel={dthBitChipLabel}
          onClear={clearBit}
        >
          <DTHBitDropdowns disabled={disableBitChain} />
        </DSCSlotCard>
        <DSCSlotCard
          slotId="hammer-series"
          dataTestid="dsc-slot-card-hammer-series"
          image={DSC_SLOT_CONFIG.DTH['hammer-series'].image}
          title={DSC_SLOT_CONFIG.DTH['hammer-series'].title}
          isFilled={!!hammerChipLabel}
          isDisabled={disableHammerChain}
          filledLabel={hammerChipLabel}
          onClear={clearHammer}
        >
          <DTHHammerDropdowns disabled={disableHammerChain} />
        </DSCSlotCard>
        <DSCSlotCard
          slotId="drillpipe"
          dataTestid="dsc-slot-card-drillpipe"
          image={DSC_SLOT_CONFIG.DTH.drillpipe.image}
          title={DSC_SLOT_CONFIG.DTH.drillpipe.title}
          isFilled={!!drillpipeChipLabel}
          filledLabel={drillpipeChipLabel}
          onClear={clearDrillpipe}
        >
          <DTHDrillpipeDropdowns />
        </DSCSlotCard>
      </div>

      {/* 5 + 6. Results tabs + active tab panel. Tab order is enforced inside
            DSCResultsTabs (it reads from dscSlotConfig.DTH_TAB_ORDER), so
            Hammers → Drill pipes → Bits is canonical. Empty tabs grey out
            (wishlist #11) rather than disappearing.

            Products + facets for the active tab are computed via
            filterProductsForTab — narrows the full DTH catalog to the
            products matching the tab's PGMJ+PGMN tuple AND the live cascade
            selections. Previously DTHDSCLayout passed no products at all, so
            every DTH tab rendered empty regardless of selection. */}
      <DTHResultsSection activeTab={activeTab} setActiveTab={setActiveTab} />
    </div>
  );
}

function DTHResultsSection({ activeTab, setActiveTab }) {
  const { state } = useDSC();
  const { dispatch: cartDispatch } = useCart();
  const { hasPermission } = useAuth();
  const { isDesktop } = useMediaQuery();
  const canOrder = hasPermission('order');
  const tabProducts = useMemo(
    () => filterProductsForTab(state.products || [], activeTab, state.selections, state.dscFilteringData),
    [state.products, activeTab, state.selections, state.dscFilteringData]
  );
  const facetIds = DSC_TAB_FACETS[activeTab] || [];

  // G4: Add to cart from DTH DSC results
  const handleAddToCart = useCallback((product) => {
    cartDispatch({
      type: 'ADD_ITEM',
      payload: {
        productId: product.itemCode,
        name: product.description,
        sku: product.itemCode,
        itemCode: product.itemCode,
        description: product.description,
        thumbnailUrl: product.imageUrl || null,
        productLine: product.productLine,
        subcategory: product.subcategory,
        quantity: 1,
      },
    });
  }, [cartDispatch]);

  return (
    <div className="flex flex-col gap-4">
      <DSCResultsTabs
        productLine="DTH"
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
      <div className="flex gap-6">
        <div className="flex-1 min-w-0">
          <DSCResultsTabPanel
            tabId={activeTab}
            products={tabProducts}
            facetIds={facetIds}
            onAddToCart={canOrder ? handleAddToCart : undefined}
          />
        </div>
        {isDesktop && canOrder && (
          <aside className="w-72 flex-shrink-0" data-testid="minicart-column">
            <MiniCart />
          </aside>
        )}
      </div>
    </div>
  );
}

